// ============================================================
//  CREW PORTAL — Driver / Sideman route access page
//  - Routes visible until 7AM UK the day they are scheduled
//  - No timer, no undo — accept once, done
// ============================================================
import React, { useEffect, useState } from "react";
import { ref, onValue, off, set as fbSet } from "firebase/database";
import { db } from "../../firebase/config";
import { useAuthStore } from "../../store/authStore";
import { useCrewStore } from "../../store/crewStore";
import { useProfileStore } from "../../store/profileStore";
import { showToast } from "../shared/Toast";
import { generateId } from "../../utils";
import type { Crew } from "../../types";

// Inline type — avoids cross-component casing issues on Windows filesystems
interface DayOffRequest {
  id: string;
  profileId: string;
  crewName: string;
  role: string;
  requestDate: string;
  note: string;
  status: "pending" | "accepted" | "declined" | "cancelled";
  declineReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

const C = {
  bg: "#0b0c0e",
  surface: "#141518",
  surface2: "#1c1e22",
  border: "rgba(255,255,255,0.07)",
  amber: "#E85520",
  amberDim: "#B83F10",
  green: "#3ecf6e",
  red: "#e05252",
  muted: "#7a7d85",
  faint: "#454850",
  text: "#e8e9eb",
};

// Hide route only when its workDate === today UK AND it is past 7AM UK
function shouldHideForCrewToday(workDate: string): boolean {
  const ukNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/London" }),
  );
  const ukHour = ukNow.getHours();
  if (ukHour < 9) return false;

  const todayISO = `${ukNow.getFullYear()}-${String(ukNow.getMonth() + 1).padStart(2, "0")}-${String(ukNow.getDate()).padStart(2, "0")}`;
  const todayDate = new Date(todayISO + "T00:00:00");

  const MONTHS: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  try {
    const parts = workDate.split(" ");
    const day = parseInt(parts[1], 10);
    const month = MONTHS[parts[2]];
    const year = parseInt(parts[3], 10);
    const routeDate = new Date(year, month, day);
    return routeDate.getTime() <= todayDate.getTime();
  } catch {
    return false;
  }
}

// ─── Calendar freeze logic ────────────────────────────────────
// Every Friday at 00:00 UK, freeze Sat→Sun+9days (the upcoming weekend + full next week)
function getFrozenWindow(): { start: string; end: string } {
  const ukNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/London" }),
  );
  const dow = ukNow.getDay(); // 0=Sun..5=Fri..6=Sat
  // Lock triggers at 12:00 noon UK on Friday, not midnight
  const isFridayBeforeNoon = dow === 5 && ukNow.getHours() < 12;
  const daysBack = dow >= 5 && !isFridayBeforeNoon ? dow - 5 : dow + 2;
  const lastFri = new Date(ukNow);
  lastFri.setHours(0, 0, 0, 0);
  lastFri.setDate(lastFri.getDate() - daysBack);
  const fs = new Date(lastFri);
  fs.setDate(fs.getDate() + 1);
  const fe = new Date(lastFri);
  fe.setDate(fe.getDate() + 9);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(fs), end: fmt(fe) };
}

// ─── Main Component ───────────────────────────────────────────
const CrewPortal: React.FC = () => {
  const { name, logout } = useAuthStore();
  const { crews, updateCrew } = useCrewStore();
  const profiles = useProfileStore((s) => s.profiles);

  const myRole = (c: Crew): "driver" | "sideman" | "sideman2" | null => {
    const n = (name || "").toLowerCase();
    // Find this user's profile ID by name+dob match
    const myProfile = profiles.find(
      (p) =>
        `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
        n,
    );
    const myId = myProfile?.id;
    // Match by profileId first (accurate), fall back to name
    if (myId) {
      if (c.driver.profileId === myId) return "driver";
      if (c.sideman.profileId === myId) return "sideman";
      if (c.sideman2?.profileId === myId) return "sideman2";
    }
    // Fallback: match by username OR full name
    const myProfile2 = profiles.find(
      (p) => (p as any).username?.toLowerCase() === n,
    );
    const checkName = (memberName: string) => {
      if (memberName.toLowerCase() === n) return true;
      // Also check if stored name matches any of this user's identifiers
      const mp =
        myProfile2 ||
        profiles.find(
          (p) =>
            `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
            n,
        );
      if (!mp) return false;
      const uname = ((mp as any).username || "").toLowerCase();
      const fname =
        `${mp.firstName}${mp.lastName ? " " + mp.lastName : ""}`.toLowerCase();
      return (
        memberName.toLowerCase() === uname || memberName.toLowerCase() === fname
      );
    };
    if (checkName(c.driver.name)) return "driver";
    if (checkName(c.sideman.name)) return "sideman";
    if (c.sideman2 && checkName(c.sideman2.name)) return "sideman2";
    return null;
  };

  const [dayOffDates, setDayOffDates] = useState<string[]>([]);

  useEffect(() => {
    const myName = (name || "").toLowerCase();
    // Check next 7 days of rb_slots for this crew member in crewOff
    const found: string[] = [];
    const unsubs: (() => void)[] = [];
    for (let i = 1; i <= 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const slotRef = ref(db, `rb_slots/${dateKey}`);
      const unsub = onValue(slotRef, (snap) => {
        const raw = snap.val();
        if (!raw || !Array.isArray(raw)) return;
        const profiles_store = raw.flatMap((s: any) => s.crewOff || []);
        // We don't have profile ids here, so we match by checking profiles store name
        // Instead store the date label for display
        const match = raw.some((s: any) =>
          (s.crewOff || []).some((entry: any) => entry.id),
        );
        // We'll handle name matching via profiles in the component
      });
      unsubs.push(unsub);
    }
    return () => unsubs.forEach((fn) => fn());
  }, [name]);

  const [offDates, setOffDates] = useState<string[]>([]);
  const [standbyDates, setStandbyDates] = useState<string[]>([]);

  // ── Day-off request form state ────────────────────────────
  const [dayOffOpen, setDayOffOpen] = useState(false);
  const [dayOffDate, setDayOffDate] = useState("");
  const [dayOffNote, setDayOffNote] = useState("");
  const [dayOffSubmitting, setDayOffSubmitting] = useState(false);
  const [myDayOffRequests, setMyDayOffRequests] = useState<DayOffRequest[]>([]);

  // Load this crew member's own day-off requests
  useEffect(() => {
    const myProfile = profiles.find(
      (p) =>
        ((p as any).username || "").toLowerCase() ===
          (name || "").toLowerCase() ||
        `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
          (name || "").toLowerCase(),
    );
    if (!myProfile) return;
    const rRef = ref(db, "dayoff_requests");
    const unsub = onValue(rRef, (snap) => {
      const raw = snap.val();
      if (!raw) {
        setMyDayOffRequests([]);
        return;
      }
      const mine = (Object.values(raw) as DayOffRequest[])
        .filter((r) => r.profileId === myProfile.id)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      setMyDayOffRequests(mine);
    });
    return () => unsub();
  }, [name, profiles]);

  const submitDayOff = async () => {
    if (!dayOffDate) {
      showToast("Please select a date");
      return;
    }
    if (!dayOffNote.trim()) {
      showToast("Please add a reason for your request");
      return;
    }
    const myProfile = profiles.find(
      (p) =>
        ((p as any).username || "").toLowerCase() ===
          (name || "").toLowerCase() ||
        `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
          (name || "").toLowerCase(),
    );
    if (!myProfile) {
      showToast("Profile not found — contact your admin");
      return;
    }
    const alreadyReq = myDayOffRequests.some(
      (r) => r.requestDate === dayOffDate && r.status !== "declined",
    );
    if (alreadyReq) {
      showToast("You already have a pending/accepted request for this date");
      return;
    }
    setDayOffSubmitting(true);
    try {
      const id = generateId();
      await fbSet(ref(db, `dayoff_requests/${id}`), {
        id,
        profileId: myProfile.id,
        crewName:
          (myProfile as any).username ||
          `${myProfile.firstName}${myProfile.lastName ? " " + myProfile.lastName : ""}`,
        role: myProfile.role,
        requestDate: dayOffDate,
        note: dayOffNote.trim(),
        status: "pending",
        declineReason: null,
        createdAt: new Date().toISOString(),
        reviewedAt: null,
      });
      setDayOffDate("");
      setDayOffNote("");
      showToast("Day-off request sent ✓");
    } catch (err: any) {
      console.error("Day-off write error:", err);
      showToast(
        err?.code === "PERMISSION_DENIED"
          ? "Permission denied — ask your admin to update Firebase rules"
          : `Error: ${err?.message || "try again"}`,
      );
    }
    setDayOffSubmitting(false);
  };

  useEffect(() => {
    const myProfile = profiles.find(
      (p) =>
        `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
          (name || "").toLowerCase() ||
        ((p as any).username || "").toLowerCase() ===
          (name || "").toLowerCase(),
    );
    if (!myProfile) return;
    const unsubs2: (() => void)[] = [];
    const collected2: Record<string, boolean> = {};
    for (let i = 0; i <= 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const slotRef = ref(db, `rb_slots/${dateKey}`);
      const unsub = onValue(slotRef, (snap) => {
        const raw = snap.val();
        if (!raw || !Array.isArray(raw)) return;
        const isStandby = raw.some((s: any) =>
          (s.standby || []).some((entry: any) => entry.id === myProfile.id),
        );
        if (isStandby) collected2[dateKey] = true;
        else delete collected2[dateKey];
        setStandbyDates(Object.keys(collected2).sort());
      });
      unsubs2.push(unsub);
    }
    return () => unsubs2.forEach((fn) => fn());
  }, [name, profiles]);

  useEffect(() => {
    const myProfile = profiles.find(
      (p) =>
        `${p.firstName}${p.lastName ? " " + p.lastName : ""}`.toLowerCase() ===
          (name || "").toLowerCase() ||
        ((p as any).username || "").toLowerCase() ===
          (name || "").toLowerCase(),
    );
    if (!myProfile) return;
    const unsubs: (() => void)[] = [];
    const collected: Record<string, boolean> = {};
    for (let i = 0; i <= 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const slotRef = ref(db, `rb_slots/${dateKey}`);
      const unsub = onValue(slotRef, (snap) => {
        const raw = snap.val();
        if (!raw || !Array.isArray(raw)) return;
        const isOff = raw.some((s: any) =>
          (s.crewOff || []).some((entry: any) => entry.id === myProfile.id),
        );
        if (isOff) collected[dateKey] = true;
        else delete collected[dateKey];
        setOffDates(Object.keys(collected).sort());
      });
      unsubs.push(unsub);
    }
    return () => unsubs.forEach((fn) => fn());
  }, [name, profiles]);

  // Hide routes scheduled for today once 7AM UK has passed; future routes always visible
  const myRows = crews
    .filter((c) => {
      const role = myRole(c);
      if (!role) return false;
      if (shouldHideForCrewToday(c.workDate)) return false;
      // Only show sideman2 row if crew actually has sideman2
      if (role === "sideman2" && !c.sideman2) return false;
      return true;
    })
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  const doAccept = (crewId: string) => {
    const crew = crews.find((c) => c.id === crewId);
    if (!crew) return;
    const role = myRole(crew);
    if (!role) return;
    updateCrew(crewId, (c) => {
      if (role === "sideman2" && c.sideman2) {
        return {
          ...c,
          sideman2: {
            ...c.sideman2,
            confirmed: true,
            confirmedAt: new Date().toISOString(),
            declined: false,
            declineNote: null,
            locked: false,
            acceptNote: null,
          },
        };
      }
      return {
        ...c,
        [role]: {
          ...c[role as "driver" | "sideman"],
          confirmed: true,
          confirmedAt: new Date().toISOString(),
          declined: false,
          declineNote: null,
          locked: false,
          acceptNote: null,
        },
      };
    });
    showToast("Route accepted!");
  };

  const findProfileByMember = (member: {
    name: string;
    profileId?: string | null;
  }) => {
    if (member.profileId) {
      const p = profiles.find((pr) => pr.id === member.profileId);
      if (p) return p;
    }
    const n = member.name.toLowerCase();
    return (
      profiles.find(
        (pr) =>
          ((pr as any).username || "").toLowerCase() === n ||
          `${pr.firstName}${pr.lastName ? " " + pr.lastName : ""}`.toLowerCase() ===
            n,
      ) || null
    );
  };

  const getPartnerPhone = (
    crew: Crew,
    role: "driver" | "sideman" | "sideman2",
  ): string | null => {
    const partner = role === "driver" ? crew.sideman : crew.driver;
    return findProfileByMember(partner)?.phone || null;
  };

  const getSideman2Phone = (crew: Crew): string | null => {
    if (!crew.sideman2) return null;
    return findProfileByMember(crew.sideman2)?.phone || null;
  };

  const getDriverPhone = (crew: Crew): string | null => {
    return findProfileByMember(crew.driver)?.phone || null;
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: C.bg,
        zIndex: 5000,
        overflowY: "auto",
        fontFamily: "'Barlow',sans-serif",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          background: C.surface,
          borderBottom: `1px solid ${C.border}`,
          padding: "16px 20px 14px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => window.location.reload()}
          >
            <img
              src="/img/SGK_logo.png"
              alt="SGK"
              style={{ width: 180, height: "auto", display: "block" }}
            />
            <div
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: 18,
                fontWeight: 800,
                textTransform: "uppercase",
                color: C.text,
              }}
            >
              Fleet<span style={{ color: C.amber }}>Ops</span>
            </div>
          </div>
          <button
            onClick={logout}
            style={{
              background: "none",
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.muted,
              fontFamily: "'Barlow',sans-serif",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            Log Out
          </button>
        </div>
        <div style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>
          Routes for <strong style={{ color: C.amber }}>{name}</strong>
        </div>
      </div>

      {/* BODY */}
      <div style={{ padding: 20, paddingBottom: 60 }}>
        <div
          style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: C.muted,
            marginBottom: 14,
          }}
        >
          Your Assigned Routes
        </div>

        {standbyDates.length > 0 &&
          standbyDates.map((dateKey) => {
            const d = new Date(dateKey + "T00:00:00");
            const label = d.toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
            });
            return (
              <div
                key={"sb-" + dateKey}
                style={{
                  background: "rgba(100,160,255,0.08)",
                  border: "1px solid rgba(100,160,255,0.4)",
                  borderLeft: "3px solid #64a0ff",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "rgba(100,160,255,0.15)",
                    border: "2px solid #64a0ff",
                    boxShadow: "0 0 10px rgba(100,160,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 22c1.1 0 2-.9 2-2H10c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
                      fill="#64a0ff"
                    />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      color: "#64a0ff",
                      marginBottom: 4,
                    }}
                  >
                    Standby
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#e8e9eb",
                      lineHeight: 1.4,
                      marginBottom: 6,
                    }}
                  >
                    You are on standby on{" "}
                    <strong style={{ color: "#64a0ff" }}>{label}</strong>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#7a7d85",
                      lineHeight: 1.55,
                    }}
                  >
                    Please be ready — we will call you if you are required.
                    Thank you.
                  </div>
                </div>
              </div>
            );
          })}

        {offDates.length > 0 &&
          offDates.map((dateKey) => {
            const d = new Date(dateKey + "T00:00:00");
            const label = d.toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
            });
            return (
              <div
                key={dateKey}
                style={{
                  background: "rgba(100,160,255,0.08)",
                  border: "1px solid rgba(100,160,255,0.3)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "rgba(100,160,255,0.15)",
                    border: "2px solid #64a0ff",
                    boxShadow: "0 0 10px rgba(100,160,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
                      stroke="#64a0ff"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      color: "#64a0ff",
                      marginBottom: 4,
                    }}
                  >
                    Day Off Scheduled
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.text,
                      lineHeight: 1.4,
                    }}
                  >
                    You are off on{" "}
                    <strong style={{ color: "#64a0ff" }}>{label}</strong>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
                    No route assigned — enjoy your day off.
                  </div>
                </div>
              </div>
            );
          })}
        <EssentialContacts />

        <div style={{ height: 24 }} />

        {myRows.length === 0 ? (
          <>
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  margin: "0 auto 16px",
                  background: C.surface,
                  borderRadius: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `1px solid ${C.border}`,
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill={C.faint}>
                  <path d="M1 3h15v13H1V3zm15 4h4l3 3v6h-7V7zm-13 9a2 2 0 1 0 4 0 2 2 0 0 0-4 0zm13 0a2 2 0 1 0 4 0 2 2 0 0 0-4 0z" />
                </svg>
              </div>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                No routes assigned yet.
                <br />
                Contact your admin.
              </p>
            </div>
            <div style={{ height: 24 }} />
          </>
        ) : (
          myRows.map((c) => (
            <CrewRow
              key={c.id + "-" + myRole(c)}
              crew={c}
              role={myRole(c)!}
              partnerPhone={getPartnerPhone(c, myRole(c)!)}
              sideman2Phone={getSideman2Phone(c)}
              driverPhone={getDriverPhone(c)}
              onAccept={() => doAccept(c.id)}
            />
          ))
        )}

        {/* ── REQUEST A DAY-OFF SECTION ── */}
        <div style={{ marginTop: 28 }}>
          <button
            onClick={() => setDayOffOpen((o) => !o)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: dayOffOpen ? "rgba(232,85,32,0.08)" : C.surface,
              border: `1px solid ${dayOffOpen ? "rgba(232,85,32,0.35)" : C.border}`,
              borderRadius: dayOffOpen ? "12px 12px 0 0" : 12,
              padding: "14px 16px",
              cursor: "pointer",
              fontFamily: "'Barlow',sans-serif",
              transition: "all 0.2s",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                fontWeight: 700,
                color: C.text,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  stroke={C.amber}
                  strokeWidth="1.8"
                />
                <path
                  d="M16 2v4M8 2v4M3 10h18"
                  stroke={C.amber}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M8 15l2 2 4-4"
                  stroke={C.amber}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Request A Day-Off
              {myDayOffRequests.filter((r) => r.status === "pending").length >
                0 && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#E85520",
                    background: "rgba(232,85,32,0.1)",
                    border: "1px solid rgba(232,85,32,0.3)",
                    borderRadius: 10,
                    padding: "1px 7px",
                  }}
                >
                  {
                    myDayOffRequests.filter((r) => r.status === "pending")
                      .length
                  }{" "}
                  pending
                </span>
              )}
            </span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                color: C.muted,
                transform: dayOffOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {dayOffOpen && (
            <div
              style={{
                background: C.surface,
                border: "1px solid rgba(232,85,32,0.25)",
                borderTop: "none",
                borderRadius: "0 0 12px 12px",
                padding: "16px",
              }}
            >
              {/* Calendar */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  color: C.muted,
                  marginBottom: 10,
                }}
              >
                Select Date <span style={{ color: C.red }}>*</span>
              </div>
              <DayOffCalendar value={dayOffDate} onChange={setDayOffDate} />

              {/* Note */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.6px",
                  color: C.muted,
                  margin: "14px 0 6px",
                }}
              >
                Reason <span style={{ color: C.red }}>*</span>
              </div>
              <textarea
                value={dayOffNote}
                onChange={(e) => setDayOffNote(e.target.value)}
                placeholder="Enter the reason for your day-off request…"
                rows={3}
                style={{
                  width: "100%",
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  color: C.text,
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 13,
                  outline: "none",
                  resize: "none",
                  boxSizing: "border-box",
                  lineHeight: 1.5,
                }}
              />

              <button
                onClick={submitDayOff}
                disabled={dayOffSubmitting || !dayOffDate || !dayOffNote.trim()}
                style={{
                  width: "100%",
                  marginTop: 12,
                  padding: "13px",
                  background:
                    dayOffSubmitting || !dayOffDate || !dayOffNote.trim()
                      ? "#24262c"
                      : C.amber,
                  border: "none",
                  borderRadius: 10,
                  color:
                    dayOffSubmitting || !dayOffDate || !dayOffNote.trim()
                      ? C.faint
                      : "#000",
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 13,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  cursor:
                    dayOffSubmitting || !dayOffDate || !dayOffNote.trim()
                      ? "not-allowed"
                      : "pointer",
                  transition: "all 0.2s",
                }}
              >
                {dayOffSubmitting ? "Sending…" : "Send Request"}
              </button>

              {/* My existing requests */}
              {myDayOffRequests.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      color: C.muted,
                      marginBottom: 10,
                    }}
                  >
                    My Requests
                  </div>
                  {myDayOffRequests.map((req) => {
                    const statusColor =
                      req.status === "accepted"
                        ? C.green
                        : req.status === "pending"
                          ? C.amber
                          : req.status === "cancelled"
                            ? "#7a7d85"
                            : C.red;
                    const statusLabel =
                      req.status === "accepted"
                        ? "Accepted ✓"
                        : req.status === "pending"
                          ? "Pending…"
                          : req.status === "cancelled"
                            ? "Cancelled"
                            : "Declined";
                    const reqDateLabel = new Date(
                      req.requestDate + "T00:00:00",
                    ).toLocaleDateString("en-GB", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    });
                    return (
                      <div
                        key={req.id}
                        style={{
                          background: C.surface2,
                          border: `1px solid ${C.border}`,
                          borderLeft: `3px solid ${statusColor}`,
                          borderRadius: 10,
                          padding: "12px 14px",
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.text,
                            }}
                          >
                            {reqDateLabel}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              color: statusColor,
                              background: `${statusColor}18`,
                              borderRadius: 6,
                              padding: "2px 8px",
                              border: `1px solid ${statusColor}40`,
                            }}
                          >
                            {statusLabel}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: C.muted }}>
                          {req.note}
                        </div>
                        {req.status === "declined" && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 11,
                              color: C.red,
                              background: "rgba(224,82,82,0.06)",
                              borderRadius: 6,
                              padding: "6px 10px",
                              border: "1px solid rgba(224,82,82,0.18)",
                            }}
                          >
                            <strong>Admin note:</strong>{" "}
                            {req.declineReason || "No reason has been given"}
                          </div>
                        )}
                        {req.status === "cancelled" && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 11,
                              color: "#7a7d85",
                            }}
                          >
                            Your day-off has been cancelled by admin.
                          </div>
                        )}
                        {req.status === "accepted" && (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 11,
                              color: C.green,
                            }}
                          >
                            Your day-off has been approved. You will be marked
                            as off for this day.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Route card ───────────────────────────────────────────────
interface RowProps {
  crew: Crew;
  role: "driver" | "sideman" | "sideman2";
  partnerPhone: string | null;
  sideman2Phone: string | null;
  driverPhone: string | null;
  onAccept: () => void;
}

const CrewRow: React.FC<RowProps> = ({
  crew,
  role,
  partnerPhone,
  sideman2Phone,
  driverPhone,
  onAccept,
}) => {
  const profiles = useProfileStore((s) => s.profiles);
  const [expanded, setExpanded] = React.useState(false);
  const me =
    role === "sideman2" ? crew.sideman2! : crew[role as "driver" | "sideman"];
  const partner = role === "driver" ? crew.sideman : crew.driver;

  const getUsername = (member: { name: string; profileId?: string | null }) => {
    if (member.profileId) {
      const p = profiles.find((pr) => pr.id === member.profileId);
      if (p && (p as any).username) return (p as any).username;
    }
    return member.name;
  };
  const pLabel = role === "driver" ? "Sideman" : "Driver";
  const partnerOk = partner.locked || partner.confirmed;
  const sideman2Ok = crew.sideman2
    ? crew.sideman2.locked || crew.sideman2.confirmed
    : false;

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        marginBottom: 16,
        overflow: "hidden",
        borderLeft: `3px solid ${me.confirmed ? C.green : "#454850"}`,
      }}
    >
      {/* Dropdown header */}
      <div
        onClick={() => setExpanded((o) => !o)}
        style={{
          padding: "16px 16px 14px",
          cursor: "pointer",
          borderBottom: expanded ? `1px solid ${C.border}` : "none",
          background: expanded ? "rgba(62,207,110,0.04)" : "transparent",
          transition: "background 0.2s",
        }}
      >
        {/* DAY / DATE */}
        <div
          style={{
            fontFamily: "'Barlow Condensed',sans-serif",
            fontSize: 26,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: "#3ecf6e",
            textShadow:
              "0 0 18px rgba(62,207,110,0.9), 0 0 40px rgba(62,207,110,0.5)",
            lineHeight: 1.1,
            marginBottom: 4,
          }}
        >
          {crew.workDate}
        </div>

        {/* TIME */}
        {crew.startTime && (
          <div
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: 22,
              fontWeight: 900,
              color: "#3ecf6e",
              textShadow:
                "0 0 14px rgba(62,207,110,0.9), 0 0 30px rgba(62,207,110,0.5)",
              letterSpacing: "0.5px",
              marginBottom: 6,
            }}
          >
            {crew.startTime}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {crew.routeNumber && (
              <span
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  color: C.amber,
                  textTransform: "uppercase",
                  background: "rgba(232,85,32,0.1)",
                  border: "1px solid rgba(232,85,32,0.3)",
                  padding: "2px 10px",
                  borderRadius: 6,
                }}
              >
                {crew.routeNumber}
              </span>
            )}
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: me.confirmed ? C.green : C.amber,
                background: me.confirmed
                  ? "rgba(62,207,110,0.1)"
                  : "rgba(232,85,32,0.1)",
                border: `1px solid ${me.confirmed ? "rgba(62,207,110,0.3)" : "rgba(232,85,32,0.3)"}`,
                padding: "2px 10px",
                borderRadius: 6,
              }}
            >
              {me.confirmed ? "✓ Accepted" : "Awaiting"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!me.confirmed && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept();
                }}
                style={{
                  padding: "8px 16px",
                  background: C.amber,
                  border: "none",
                  borderRadius: 8,
                  color: "#000",
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Accept
              </button>
            )}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                color: "#3ecf6e",
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
                flexShrink: 0,
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "14px 16px" }}>
          {crew.photo && (
            <img
              src={crew.photo}
              alt="Crew"
              style={{
                width: "100%",
                height: 120,
                objectFit: "cover",
                borderRadius: 10,
                marginBottom: 12,
                border: `1px solid ${C.border}`,
              }}
            />
          )}

          {/* Role + Partner */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: crew.sideman2 ? "1fr 1fr 1fr" : "1fr 1fr",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <InfoBox
              label={
                role === "driver"
                  ? "Driver"
                  : role === "sideman"
                    ? "Sideman"
                    : "Sideman 2"
              }
              value={getUsername(me)}
            />
            <InfoBox label={pLabel} value={getUsername(partner)} />
            {crew.sideman2 && role === "driver" && (
              <InfoBox label="Sideman 2" value={getUsername(crew.sideman2)} />
            )}
            {crew.sideman2 && role === "sideman" && (
              <InfoBox
                label="Other Sideman"
                value={getUsername(crew.sideman2)}
              />
            )}
            {crew.sideman2 && role === "sideman2" && (
              <InfoBox label="Sideman" value={getUsername(crew.sideman)} />
            )}
          </div>

          {/* Partner phone */}
          <div
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 9,
              padding: "10px 14px",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                stroke={partnerPhone ? C.amber : C.faint}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  color: C.muted,
                  marginBottom: 3,
                }}
              >
                {pLabel}'s Phone
              </div>
              {partnerPhone ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <a
                    href={`tel:${partnerPhone}`}
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.amber,
                      textDecoration: "none",
                    }}
                  >
                    {partnerPhone}
                  </a>
                  <a
                    href={`tel:${partnerPhone}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "rgba(232,85,32,0.15)",
                      border: "1px solid rgba(232,85,32,0.3)",
                      textDecoration: "none",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                        stroke="#E85520"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </a>
                </div>
              ) : (
                <span
                  style={{ fontSize: 13, color: C.faint, fontStyle: "italic" }}
                >
                  Not provided
                </span>
              )}
            </div>
          </div>

          {/* ── Route confirmation notice ── */}
          <div
            style={{
              background: "rgba(232,85,32,0.08)",
              border: "1px solid rgba(232,85,32,0.4)",
              borderLeft: "3px solid #E85520",
              borderRadius: 9,
              padding: "12px 14px",
              marginBottom: 12,
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              style={{ flexShrink: 0, marginTop: 1 }}
            >
              <path
                d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="#E85520"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  color: "#E85520",
                  marginBottom: 5,
                }}
              >
                Action Required
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#e8e9eb",
                  lineHeight: 1.55,
                }}
              >
                Please call your driver / sideman and confirm your route for
                tomorrow. If your driver / sideman does not answer by{" "}
                <strong style={{ color: "#E85520" }}>7pm</strong> please call{" "}
                <strong style={{ color: "#E85520" }}>Deep</strong>. Thank you.
              </div>
            </div>
          </div>

          {/* Sideman 2 phone — shown to driver */}
          {crew.sideman2 && role === "driver" && (
            <div
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 9,
                padding: "10px 14px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                  stroke={sideman2Phone ? C.amber : C.faint}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: C.muted,
                    marginBottom: 3,
                  }}
                >
                  Sideman 2's Phone
                </div>
                {sideman2Phone ? (
                  <a
                    href={`tel:${sideman2Phone}`}
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.amber,
                      textDecoration: "none",
                    }}
                  >
                    {sideman2Phone}
                  </a>
                ) : (
                  <span
                    style={{
                      fontSize: 13,
                      color: C.faint,
                      fontStyle: "italic",
                    }}
                  >
                    Not provided
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Driver phone — shown to sidemen */}
          {(role === "sideman" || role === "sideman2") && (
            <div
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 9,
                padding: "10px 14px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                  stroke={driverPhone ? C.amber : C.faint}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: C.muted,
                    marginBottom: 3,
                  }}
                >
                  Driver's Phone
                </div>
                {driverPhone ? (
                  <a
                    href={`tel:${driverPhone}`}
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.amber,
                      textDecoration: "none",
                    }}
                  >
                    {driverPhone}
                  </a>
                ) : (
                  <span
                    style={{
                      fontSize: 13,
                      color: C.faint,
                      fontStyle: "italic",
                    }}
                  >
                    Not provided
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Partner status */}
          <div
            style={{
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 9,
              padding: "10px 12px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>
              {pLabel}'s status
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 20,
                background: partnerOk
                  ? "rgba(62,207,110,0.1)"
                  : "rgba(232,85,32,0.1)",
                border: `1px solid ${partnerOk ? "rgba(62,207,110,0.3)" : "rgba(232,85,32,0.3)"}`,
                color: partnerOk ? C.green : C.amber,
              }}
            >
              {partnerOk ? "Accepted" : "Awaiting"}
            </span>
          </div>
          {crew.sideman2 && (
            <div
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 9,
                padding: "10px 12px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>
                {role === "driver"
                  ? "Sideman 2's status"
                  : "Other Sideman's status"}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 20,
                  background: sideman2Ok
                    ? "rgba(62,207,110,0.1)"
                    : "rgba(232,85,32,0.1)",
                  border: `1px solid ${sideman2Ok ? "rgba(62,207,110,0.3)" : "rgba(232,85,32,0.3)"}`,
                  color: sideman2Ok ? C.green : C.amber,
                }}
              >
                {sideman2Ok ? "Accepted" : "Awaiting"}
              </span>
            </div>
          )}

          {/* Start time */}
          {crew.startTime && (
            <div
              style={{
                background: "rgba(232,85,32,0.08)",
                border: "1px solid rgba(232,85,32,0.25)",
                borderRadius: 9,
                padding: "10px 14px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke={C.amber}
                  strokeWidth="2"
                />
                <path
                  d="M12 6v6l4 2"
                  stroke={C.amber}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: C.amberDim,
                  }}
                >
                  Start Time
                </div>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: 17,
                    fontWeight: 800,
                    color: C.amber,
                  }}
                >
                  {crew.startTime}
                </div>
              </div>
            </div>
          )}

          {/* Admin note — always visible until route disappears, only shown to correct role */}
          {(me as any).adminNote && (me as any).adminNote.trim() && (
            <div
              style={{
                background: "rgba(62,207,110,0.08)",
                border: "1px solid rgba(62,207,110,0.25)",
                borderRadius: 9,
                padding: "10px 14px",
                marginBottom: 12,
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                style={{ flexShrink: 0, marginTop: 2 }}
              >
                <path
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  stroke={C.green}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    color: C.green,
                    marginBottom: 3,
                  }}
                >
                  Note from Admin
                </div>
                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
                  {(me as any).adminNote}
                </div>
              </div>
            </div>
          )}

          {me.confirmed ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 14px",
                borderRadius: 9,
                background: "rgba(62,207,110,0.1)",
                border: "1px solid rgba(62,207,110,0.3)",
                color: C.green,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Route accepted — see you at {crew.startTime || "start time"}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: 9,
                  marginBottom: 12,
                  background: "rgba(232,85,32,0.1)",
                  border: "1px solid rgba(232,85,32,0.3)",
                  color: C.amber,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 6v6l4 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Awaiting your acceptance
              </div>

              {/* ── TOOLKIT & CUTTER CHECK ── */}
              <div
                style={{
                  background: "#1c1e22",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    color: "#7a7d85",
                    marginBottom: 12,
                    textAlign: "center",
                  }}
                >
                  ✔ Please make sure to check you have the following before
                  starting your Route:
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      background: "#141518",
                      borderRadius: 10,
                      padding: "12px 8px",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <img
                      src="/img/DrillKit.png"
                      alt="DrillKit"
                      style={{
                        width: 145,
                        height: 145,
                        objectFit: "contain",
                        borderRadius: 8,
                      }}
                    />
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#e8e9eb",
                        textAlign: "center",
                        lineHeight: 1.3,
                      }}
                    >
                      Your DrillKit
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      background: "#141518",
                      borderRadius: 10,
                      padding: "12px 8px",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <img
                      src="/img/Cutter.png"
                      alt="Cutter"
                      style={{
                        width: 145,
                        height: 145,
                        objectFit: "contain",
                        borderRadius: 8,
                      }}
                    />
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#e8e9eb",
                        textAlign: "center",
                        lineHeight: 1.3,
                      }}
                    >
                      Your Cutter
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    background: "#141518",
                    borderRadius: 10,
                    padding: "12px 8px",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <img
                    src="/img/ToolKit.png"
                    alt="ToolKit"
                    style={{
                      width: 170,
                      height: 170,
                      objectFit: "contain",
                      borderRadius: 8,
                    }}
                  />
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#e8e9eb",
                      textAlign: "center",
                      lineHeight: 1.3,
                    }}
                  >
                    Your ToolKit
                  </div>
                </div>
              </div>

              <button
                onClick={onAccept}
                style={{
                  width: "100%",
                  padding: "15px",
                  borderRadius: 10,
                  fontFamily: "'Barlow',sans-serif",
                  fontSize: 15,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  cursor: "pointer",
                  background: C.amber,
                  color: "#000",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Accept Route
              </button>
            </>
          )}

          {/* Stops */}
          {crew.deliveries && crew.deliveries.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.7px",
                  color: C.muted,
                  marginBottom: 8,
                }}
              >
                Stops ({crew.deliveries.length})
              </div>
              {crew.deliveries.map((d, i) => (
                <div
                  key={d.id}
                  style={{
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    marginBottom: 6,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontSize: 13,
                    color: C.text,
                    fontWeight: 500,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.amber,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                      paddingTop: 1,
                    }}
                  >
                    Stop {i + 1}
                  </span>
                  <span>{d.address}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InfoBox: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      background: "#1c1e22",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: 9,
      padding: "10px 12px",
    }}
  >
    <div
      style={{
        fontSize: 9,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        color: "#E85520",
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: "#e8e9eb",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  </div>
);

const EssentialContacts: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      style={{
        marginTop: 16,
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "13px 16px",
          background: "#141518",
          border: "none",
          cursor: "pointer",
          fontFamily: "'Barlow',sans-serif",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
              stroke="#E85520"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#e8e9eb",
            }}
          >
            Essential Contact Numbers
          </span>
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          style={{
            color: "#7a7d85",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open && (
        <div
          style={{
            background: "#1c1e22",
            padding: "12px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {[
            { label: "SGK Route Issue Number", number: "01902 214525" },
            { label: "SGK Office Customer Number", number: "01902 962575" },
          ].map((c) => (
            <div
              key={c.number}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#141518",
                borderRadius: 9,
                padding: "10px 14px",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.7px",
                    color: "#7a7d85",
                    marginBottom: 3,
                  }}
                >
                  {c.label}
                </div>
                <a
                  href={`tel:${c.number.replace(/\s/g, "")}`}
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#E85520",
                    textDecoration: "none",
                  }}
                >
                  {c.number}
                </a>
              </div>
              <a
                href={`tel:${c.number.replace(/\s/g, "")}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "rgba(232,85,32,0.15)",
                  border: "1px solid rgba(232,85,32,0.3)",
                  textDecoration: "none",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.12 6.12l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                    stroke="#E85520"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Day-Off Calendar ────────────────────────────────────────
interface CalendarProps {
  value: string;
  onChange: (date: string) => void;
}
const DayOffCalendar: React.FC<CalendarProps> = ({ value, onChange }) => {
  const ukNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/London" }),
  );
  const todayStr = `${ukNow.getFullYear()}-${String(ukNow.getMonth() + 1).padStart(2, "0")}-${String(ukNow.getDate()).padStart(2, "0")}`;

  const { start: frozenStart, end: frozenEnd } = getFrozenWindow();

  // Start at the first month with an available (selectable) date
  const firstAvail = new Date(frozenEnd + "T00:00:00");
  firstAvail.setDate(firstAvail.getDate() + 1);

  const [viewYear, setViewYear] = React.useState(firstAvail.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(firstAvail.getMonth());

  const isDisabled = (ds: string): boolean =>
    ds <= todayStr || (ds >= frozenStart && ds <= frozenEnd);

  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const startOffset = (firstDow + 6) % 7; // Mon-start
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(
      `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    );
  }

  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  return (
    <div
      style={{
        background: "#1c1e22",
        borderRadius: 12,
        padding: 12,
        userSelect: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <button
          onClick={prevMonth}
          style={{
            background: "none",
            border: "none",
            color: "#7a7d85",
            cursor: "pointer",
            fontSize: 20,
            padding: "0 8px",
            lineHeight: 1,
          }}
        >
          ‹
        </button>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#e8e9eb" }}>
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          onClick={nextMonth}
          style={{
            background: "none",
            border: "none",
            color: "#7a7d85",
            cursor: "pointer",
            fontSize: 20,
            padding: "0 8px",
            lineHeight: 1,
          }}
        >
          ›
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
          marginBottom: 5,
        }}
      >
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 9,
              fontWeight: 800,
              color: "#454850",
              textTransform: "uppercase",
              padding: "2px 0",
            }}
          >
            {d}
          </div>
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
        }}
      >
        {cells.map((ds, i) => {
          if (!ds) return <div key={"e" + i} />;
          const disabled = isDisabled(ds);
          const selected = ds === value;
          const frozen = ds >= frozenStart && ds <= frozenEnd;
          const past = ds <= todayStr;
          return (
            <button
              key={ds}
              onClick={() => !disabled && onChange(ds)}
              title={
                frozen ? "Not available — too soon" : past ? "Past date" : ""
              }
              style={{
                padding: "7px 0",
                borderRadius: 6,
                border: selected
                  ? "2px solid #E85520"
                  : "1px solid transparent",
                background: selected
                  ? "rgba(232,85,32,0.18)"
                  : frozen
                    ? "rgba(255,255,255,0.01)"
                    : "transparent",
                color: selected ? "#E85520" : disabled ? "#2a2c30" : "#e8e9eb",
                fontSize: 12,
                fontWeight: selected ? 800 : 400,
                cursor: disabled ? "not-allowed" : "pointer",
                textAlign: "center",
                opacity: disabled ? 0.3 : 1,
                transition: "background 0.1s",
              }}
            >
              {new Date(ds + "T00:00:00").getDate()}
            </button>
          );
        })}
      </div>
      <div
        style={{
          marginTop: 8,
          display: "flex",
          gap: 12,
          justifyContent: "center",
        }}
      >
        <span
          style={{
            fontSize: 9,
            color: "#454850",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: "#2a2c30",
              display: "inline-block",
            }}
          />
          Past / Not available
        </span>
        <span
          style={{
            fontSize: 9,
            color: "#E85520",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: "rgba(232,85,32,0.18)",
              border: "1px solid #E85520",
              display: "inline-block",
            }}
          />
          Selected
        </span>
      </div>
    </div>
  );
};

export default CrewPortal;
